import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Typography } from '@one-thd/sui-atomic-components';
import ErrorBoundary from '../../contexts/ErrorBoundary';
import { BannerContext, BannerProvider } from '../../contexts/BannerContext';
import PreferenceCard from '../PreferenceCard/PreferenceCard';
import { PHONE_TITLE, phoneTableHeaders } from '../tableData';
import { EPCContext } from '../../contexts/epcContext';
import { OptOutOfAllModal } from './OptOutOfAllModal';
import constants, { NO, PENDING, YES } from '../../constants/constants';
import {
  convertSubsToPrefs,
  getSubscriptions, uncheckCheckBoxes, unsubscribeFromAllSmsSubscriptions,
  updateTextPreferences
} from '../../common/sharedFunctions';
import { addAnalyticEvent } from '../../analytics/common';
import { smsAnalytics } from '../../analytics/save';
import bannerConstants from '../../constants/bannerConstants';
import {
  ConsentConfirmationModal
} from './ConsentConfirmationModal';
import { SignUpModal } from '../SignUpModal/SignUpModal';

export const unsubFromSmsButtonTestId = 'unsubFromSmsButton';

export function Preferences({ isSearchedInitiated }) {
  const {
    openSuccessAlert,
    openErrorAlert,
    hideBanners,
    openPendingAlert,
    closePendingAlert,
    openOptOutOfAllBanner,
    closeOptOutOfAllBanner,
  } = useContext(BannerContext);

  const {
    phone, setPhone, isMobile,
    smsTelemarketingTableData, setSmsTelemarketingTableData, pending,
  } = useContext(EPCContext);

  const [selectedItems, setSelectedItems] = useState([]);
  const [unsubscribeToggle, setUnsubscribeToggle] = useState(false);

  const [togglePromoConsent, setTogglePromoConsent] = useState(false);
  const [toggleProConsent, setToggleProConsent] = useState(false);
  const [toggleConsentConfirmationModal, setConsentConfirmationModal] = useState(false);
  const [toggleSignUp, setSignUp] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [hasOnlyOptIn, setHasOnlyOptIn] = useState(false);
  const [telemarketingCard, setTelemarketingCard] = useState(false);

  const determinePendingText = () => {
    const pro = pending.find((item) => item && item.type === constants.PRO)?.status;
    const promotion = pending.find((item) => item && item.type === constants.PROMOTIONS)?.status;
    if (pro === PENDING && promotion === PENDING) {
      return constants.PRO_PROMO_PENDING;
    }
    if (pro === PENDING) {
      return constants.PRO_PENDING;
    }
    if (promotion === PENDING) {
      return constants.PROMO_PENDING;
    }
    return null;
  };

  useEffect(() => {
    closeOptOutOfAllBanner();
    if (pending.length > 0) {
      openPendingAlert(determinePendingText(), phone, smsTelemarketingTableData);
    } else {
      closePendingAlert();
    }
  }, [pending]);

  useEffect(() => {
    if (smsTelemarketingTableData[PHONE_TITLE].value === NO) {
      setTelemarketingCard(true);
    }
  }, [pending]);

  const handleOptOutCancel = () => {
    setUnsubscribeToggle(false);
  };
  const openUnsubscribeModal = () => {
    hideBanners();
    setUnsubscribeToggle(true);
  };

  const handleSelectedItem = (item, checked) => {
    if (!checked) {
      openOptOutOfAllBanner(true);
    }
  };

  const handleOptOutOfAll = (phoneNumber, res) => {
    hideBanners();
    setTelemarketingCard(true);
    openSuccessAlert(bannerConstants.FCC_OPTOUT, phoneNumber);
    setSmsTelemarketingTableData(uncheckCheckBoxes(smsTelemarketingTableData));
    setPhone(phoneNumber);
    setUnsubscribeToggle(false);
    setSelectedItems([]);
  };

  function containsOptIn(list, table) {
    return list.some((item) => table[item].value === YES);
  }

  function callUnsubFromAll(phoneInputValue) {
    setSmsTelemarketingTableData(uncheckCheckBoxes(smsTelemarketingTableData));
    unsubscribeFromAllSmsSubscriptions(phoneInputValue, (res) => {
      handleOptOutOfAll(phoneInputValue, res);
    }, (err) => {
      openErrorAlert(constants.GENERIC_ERROR);
    });
    setSelectedItems([]);
  }

  const callBackend = (phoneInputValue) => {
    if (selectedItems.length > 0 && containsOptIn(selectedItems, smsTelemarketingTableData)) {
      updateTextPreferences({
        phoneNumber: phoneInputValue,
        subscriptions: selectedItems,
        table: smsTelemarketingTableData,
        callback: (response) => {
          addAnalyticEvent(smsAnalytics(selectedItems));
          if (phone !== phoneInputValue) {
            setPhone(phoneInputValue);
          }
          setConsentConfirmationModal(false);
          setToggleProConsent(false);
          setTogglePromoConsent(false);
          setSelectedItems([]);
          setSmsTelemarketingTableData(convertSubsToPrefs(smsTelemarketingTableData, getSubscriptions(response)));
          if (hasOnlyOptIn) {
            openSuccessAlert(bannerConstants.TEST_MESSAGE_OPT_IN);
          } else {
            openSuccessAlert(bannerConstants.PREFERENCES_UPDATED);
          }
        },
        err: (err) => {
          openErrorAlert(constants.GENERIC_ERROR);
        },
      });
    } else {
      callUnsubFromAll(phoneInputValue);
    }
  };

  const handleConsentConfirmationConfirm = () => {
    callBackend(phoneInput);
  };

  const handleConfirmationStep = (phoneInputValue) => {
    let hasOptOut = selectedItems.some((item) => smsTelemarketingTableData[item].value === NO);

    if (hasOptOut) {
      callUnsubFromAll(phoneInputValue);
    } else {
      let onlyOptIn = false;
      let hasProConsent = false;
      let hasPromoConsent = false;

      selectedItems.forEach((item) => {
        let tableItem = smsTelemarketingTableData[item];
        if (tableItem.value === YES) {
          onlyOptIn = true;
          if (tableItem.type === constants.PRO) {
            hasProConsent = true;
          }
          if (tableItem.type === constants.PROMOTIONS) {
            hasPromoConsent = true;
          }
        }
      });
      setHasOnlyOptIn(onlyOptIn);

      if (hasProConsent || hasPromoConsent) {
        setConsentConfirmationModal(true);
        setToggleProConsent(hasProConsent);
        setTogglePromoConsent(hasPromoConsent);
        setPhoneInput(phoneInputValue);
      } else {
        callBackend(phoneInputValue);
      }
    }
  };

  const handleSaveChanges = () => {
    if (selectedItems.length > 0) {
      hideBanners();
      if (!phone) {
        setSignUp(true);
      } else {
        handleConfirmationStep(phone);
      }
    }
  };

  const handleCancel = () => {
    const tableData = uncheckCheckBoxes(smsTelemarketingTableData, selectedItems);
    setSmsTelemarketingTableData(tableData);
    selectedItems.forEach((item) => {
      if (smsTelemarketingTableData[item].type === constants.PRO) {
        setToggleProConsent(false);
      }
      if (smsTelemarketingTableData[item].type === constants.PROMOTIONS) {
        setTogglePromoConsent(false);
      }
    });
    setSelectedItems([]);
    setConsentConfirmationModal(false);
  };

  const doNotCall = () => {
    return (
      <a href="https://www.homedepot.com/c/Do_Not_Call" style={{ color: 'black' }}><u>Do Not Call Policy</u></a>
    );
  };

  return (
    <div className="sui-grid sui-gap-4">
      <Typography variant="h2" weight="display">
        MARKETING TEXT MESSAGES & PHONE CALLS
      </Typography>

      <Typography variant="body-base" height="none">
        We will text you information about how to opt into (and out of) marketing text
        messages
        when you save changes. By opting out of Phone Calls, you will be added to The Home
        Depot&apos;s
        Do Not Call list. The Home Depot may still call you on non-promotional and
        non-marketing
        matters. If wishing to learn more, view the {doNotCall()}. To edit your
        preferences,
        click on the individual preference options below. An opt out of one option will be
        treated
        as an opt out of all preferences below.
      </Typography>

      {isSearchedInitiated && (
        <div className="sui-grid" data-testid="sms-sub-table">
          <PreferenceCard
            isMobile={isMobile}
            headerColumn={phoneTableHeaders}
            tableData={smsTelemarketingTableData}
            name="text"
            setSelectedItems={setSelectedItems}
            setTableData={setSmsTelemarketingTableData}
            handleSelectedItem={handleSelectedItem}
            telemarketingCard={telemarketingCard}
          />
          <div className="sui-grid sui-grid-cols-8">
            <div className="hidden sm:sui-col-span-4 md:sui-col-span-5" />
            <div className="sui-col-span-8 sm:sui-col-span-4 md:sui-col-span-3 sui-grid sui-gap-y-4">
              <Button
                data-testid="updateTextPreferenceButton"
                fullWidth
                onClick={handleSaveChanges}
              >
                Save Changes
              </Button>
              <Button
                data-testid={unsubFromSmsButtonTestId}
                fullWidth
                variant="secondary"
                onClick={openUnsubscribeModal}
              >
                Opt Out of All Marketing Texts & Phone Calls
              </Button>
            </div>
          </div>
        </div>
      )}
      {!isSearchedInitiated && (
        <div className="sui-grid sui-grid-cols-8">
          <div className="hidden sm:sui-col-span-4 md:sui-col-span-5" />
          <div className="sui-col-span-8 sm:sui-col-span-4 md:sui-col-span-3">
            <Button
              data-testid={unsubFromSmsButtonTestId}
              fullWidth
              variant="secondary"
              onClick={openUnsubscribeModal}
            >
              Opt Out of All Marketing Texts &amp; Phone Calls
            </Button>
          </div>
        </div>
      )}
      {unsubscribeToggle && (
        <OptOutOfAllModal
          handleClose={handleOptOutCancel}
          handleSuccess={handleOptOutOfAll}
          searchedPhone={phone}
        />
      )}
      {toggleConsentConfirmationModal && (
        <ConsentConfirmationModal
          confirm={handleConsentConfirmationConfirm}
          cancel={handleCancel}
          isProConsent={toggleProConsent}
          isPromotionsConsent={togglePromoConsent}
          isMobile={isMobile}
        />
      )}
      {toggleSignUp && (
        <SignUpModal
          handleSignUpCallBack={handleConfirmationStep}
          type={constants.SMS}
          selectedItems={selectedItems}
          tableData={smsTelemarketingTableData}
          setSignUp={setSignUp}
          setSelectedItems={setSelectedItems}
          setTableData={setSmsTelemarketingTableData}
        />
      )}
    </div>
  );
}

export function SmsTelemarketingPreferences({ isSearchedInitiated }) {
  return (
    <ErrorBoundary>
      <BannerProvider>
        <Preferences isSearchedInitiated={isSearchedInitiated} />
      </BannerProvider>
    </ErrorBoundary>
  );
}

const propTypes = {
  isSearchedInitiated: PropTypes.bool,
};

const defaultProps = {
  isSearchedInitiated: false,
};

SmsTelemarketingPreferences.propTypes = propTypes;
Preferences.propTypes = propTypes;

SmsTelemarketingPreferences.defaultProps = defaultProps;
Preferences.defaultProps = defaultProps;
